import { Helmet } from "react-helmet-async";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { Link } from "react-router-dom";
import { Calendar, Users, Heart, XCircle, Sparkles, ArrowRight, Globe } from "lucide-react";

const AltspaceVRHistory = () => {
  const schemaMarkup = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": "AltspaceVR History: A Pioneering Social VR Platform That Closed Its Doors",
    "description": "The complete history of AltspaceVR, from its 2013 founding to Microsoft's acquisition in 2017 and eventual shutdown in March 2023.",
    "author": {
      "@type": "Organization",
      "name": "Chill Study Work"
    },
    "publisher": {
      "@type": "Organization",
      "name": "Chill Study Work",
      "url": "https://chillstudy.work"
    },
    "datePublished": "2025-01-01",
    "dateModified": "2025-01-01",
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": "https://chillstudy.work/altspacevr/history/"
    },
    "about": {
      "@type": "SoftwareApplication",
      "name": "AltspaceVR",
      "applicationCategory": "SocialApplication",
      "operatingSystem": "VR Headsets, Windows"
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>AltspaceVR History: A Pioneering Social VR Platform That Closed Its Doors | CSW</title>
        <meta name="description" content="The complete history of AltspaceVR, from its 2013 founding to Microsoft's acquisition in 2017 and eventual shutdown in March 2023." />
        <meta name="keywords" content="AltspaceVR history, AltspaceVR shutdown, Microsoft Mesh, social VR platform, virtual reality community, Eric Romo" />
        <link rel="canonical" href="https://chillstudy.work/altspacevr/history/" />
        <meta property="og:title" content="AltspaceVR History: A Pioneering Social VR Platform That Closed Its Doors" />
        <meta property="og:description" content="The complete history of AltspaceVR, from its 2013 founding to Microsoft's acquisition and shutdown in 2023." />
        <meta property="og:url" content="https://chillstudy.work/altspacevr/history/" />
        <meta property="og:type" content="article" />
        <script type="application/ld+json">
          {JSON.stringify(schemaMarkup)}
        </script>
      </Helmet>

      <Header />

      <main className="pt-24 pb-16">
        <div className="container mx-auto px-4 max-w-4xl">
          {/* Breadcrumb */}
          <nav className="mb-8 text-sm" aria-label="Breadcrumb">
            <ol className="flex items-center space-x-2 text-muted-foreground">
              <li><Link to="/" className="hover:text-primary transition-colors">Home</Link></li>
              <li>/</li>
              <li><Link to="/altspacevr/" className="hover:text-primary transition-colors">AltspaceVR Alternative</Link></li>
              <li>/</li>
              <li className="text-foreground">History</li>
            </ol>
          </nav>

          {/* Hero Section */}
          <header className="mb-12">
            <h1 className="text-4xl md:text-5xl font-bold mb-6 bg-gradient-to-r from-primary via-accent to-primary bg-clip-text text-transparent">
              AltspaceVR: A Pioneering Social VR Platform That Closed Its Doors
            </h1>
            <p className="text-xl text-muted-foreground leading-relaxed">
              Before "metaverse" became a buzzword, <a href="https://en.wikipedia.org/wiki/AltspaceVR" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">AltspaceVR</a> showed 
              the world what social virtual reality could be. Launched in 2013, AltspaceVR allowed people to gather as avatars in shared 3D environments 
              to attend comedy shows, concerts, church services and classes.
            </p>
          </header>

          {/* Timeline Section */}
          <section className="mb-16">
            <div className="space-y-8">
              {/* Origins */}
              <article className="bg-card rounded-2xl p-8 border border-border/50 shadow-lg">
                <div className="flex items-center gap-3 mb-4">
                  <div className="p-2 bg-primary/10 rounded-lg">
                    <Sparkles className="w-6 h-6 text-primary" />
                  </div>
                  <h2 className="text-2xl font-bold">Birth and Growth of a Social VR Pioneer (2013-2017)</h2>
                </div>
                <p className="text-muted-foreground leading-relaxed mb-4">
                  AltspaceVR was founded by Eric Romo and a small team in 2013. The platform launched to the public in 2015 and was among the 
                  first consumer VR services to focus on <strong className="text-foreground">social interaction rather than gaming</strong>.
                </p>
                <p className="text-muted-foreground leading-relaxed mb-4">
                  Users created avatars and entered virtual venues—like campfires, comedy clubs and conference halls—where they could talk using 
                  spatialised voice chat. AltspaceVR quickly became known for its wide variety of user-generated events, from open-mic nights 
                  and trivia contests to meditation sessions and book clubs.
                </p>
                <p className="text-muted-foreground leading-relaxed">
                  The platform was accessible on multiple VR headsets and even in "2D mode" on a computer, making it inclusive for those without VR gear.
                </p>
              </article>

              {/* Microsoft Acquisition */}
              <article className="bg-card rounded-2xl p-8 border border-border/50 shadow-lg">
                <div className="flex items-center gap-3 mb-4">
                  <div className="p-2 bg-accent/10 rounded-lg">
                    <Calendar className="w-6 h-6 text-accent" />
                  </div>
                  <h2 className="text-2xl font-bold">Microsoft's Acquisition (2017)</h2>
                </div>
                <p className="text-muted-foreground leading-relaxed mb-4">
                  In 2017, facing financial difficulties, AltspaceVR was acquired by{" "}
                  <a href="https://news.microsoft.com/2017/10/03/microsoft-acquires-altspacevr/" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Microsoft</a>. 
                  Under Microsoft's ownership, the platform continued to operate largely independently.
                </p>
                <p className="text-muted-foreground leading-relaxed">
                  It hosted thousands of events and developed a loyal community, with some users reporting life-changing experiences such as 
                  forming long-term friendships and even marriages. AltspaceVR became a go-to platform for educators; groups like{" "}
                  <a href="https://educatorsinvr.com/" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Educators in VR</a> held 
                  classes and meet-ups in the virtual space.
                </p>
              </article>

              {/* Why It Was Special */}
              <article className="bg-card rounded-2xl p-8 border border-border/50 shadow-lg">
                <div className="flex items-center gap-3 mb-4">
                  <div className="p-2 bg-pink-500/10 rounded-lg">
                    <Heart className="w-6 h-6 text-pink-500" />
                  </div>
                  <h2 className="text-2xl font-bold">Why It Was Special</h2>
                </div>
                <div className="space-y-4 text-muted-foreground">
                  <div>
                    <h3 className="font-semibold text-foreground mb-1">Shared Presence</h3>
                    <p>AltspaceVR delivered a sense of "being there" with others. Spatial audio and expressive avatars made conversations feel natural.</p>
                  </div>
                  <div>
                    <h3 className="font-semibold text-foreground mb-1">User-Generated Events</h3>
                    <p>Anyone could host an event—karaoke nights, religious services, mindfulness sessions—fostering a diverse calendar of activities.</p>
                  </div>
                  <div>
                    <h3 className="font-semibold text-foreground mb-1">Cross-Platform Access</h3>
                    <p>The platform supported Oculus Rift, HTC Vive, Windows Mixed Reality and 2D desktop mode, lowering the barrier to entry.</p>
                  </div>
                  <div>
                    <h3 className="font-semibold text-foreground mb-1">Community Bonds</h3>
                    <p>AltspaceVR's small, passionate user base formed tight-knit groups. People celebrated birthdays, supported each other through hard times and even held weddings in VR.</p>
                  </div>
                </div>
              </article>

              {/* The Shutdown */}
              <article className="bg-card rounded-2xl p-8 border border-border/50 shadow-lg">
                <div className="flex items-center gap-3 mb-4">
                  <div className="p-2 bg-destructive/10 rounded-lg">
                    <XCircle className="w-6 h-6 text-destructive" />
                  </div>
                  <h2 className="text-2xl font-bold">The Shutdown (March 10, 2023)</h2>
                </div>
                <p className="text-muted-foreground leading-relaxed mb-4">
                  On January 20, 2023, the AltspaceVR team announced in a blog post that the platform would sunset on{" "}
                  <strong className="text-foreground">March 10, 2023</strong>. Microsoft explained that it wanted to focus resources on{" "}
                  <a href="https://www.microsoft.com/en-us/mesh" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Microsoft Mesh</a>, 
                  a business-oriented mixed-reality platform, and that maintaining AltspaceVR was no longer feasible.
                </p>
                <p className="text-muted-foreground leading-relaxed mb-4">
                  The closure came as part of broader layoffs in Microsoft's mixed-reality division. The team encouraged users to host final events 
                  and download their worlds before the shutdown. Many were heartbroken; they had built communities and memories over years.
                </p>
                <p className="text-muted-foreground leading-relaxed">
                  The news sparked grief and frustration in the VR community, with users lamenting the loss of a unique social space.
                </p>
              </article>

              {/* Community Reaction */}
              <article className="bg-card rounded-2xl p-8 border border-border/50 shadow-lg">
                <div className="flex items-center gap-3 mb-4">
                  <div className="p-2 bg-primary/10 rounded-lg">
                    <Users className="w-6 h-6 text-primary" />
                  </div>
                  <h2 className="text-2xl font-bold">Community Reaction and Legacy</h2>
                </div>
                <p className="text-muted-foreground leading-relaxed mb-4">
                  In the weeks before the shutdown, AltspaceVR was filled with farewell events. Users held closing ceremonies, memorials and 
                  final get-togethers. The community's sorrow underscored how deeply the platform had impacted people's lives.
                </p>
                <p className="text-muted-foreground leading-relaxed mb-4">
                  Critics argued that Microsoft had neglected AltspaceVR, but others acknowledged that the niche user base made it difficult to sustain.
                </p>
                <p className="text-muted-foreground leading-relaxed">
                  Although AltspaceVR is gone, former users migrated to other platforms like{" "}
                  <a href="https://www.meta.com/horizon-worlds/" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Horizon Worlds</a>,{" "}
                  <a href="https://hello.vrchat.com/" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">VRChat</a>, and{" "}
                  <a href="https://spatial.io/" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Spatial</a>. 
                  AltspaceVR demonstrated the emotional power of shared virtual spaces—its closure is a reminder that even pioneering platforms can disappear when corporate priorities shift.
                </p>
              </article>

              {/* What Remains */}
              <article className="bg-card rounded-2xl p-8 border border-border/50 shadow-lg">
                <div className="flex items-center gap-3 mb-4">
                  <div className="p-2 bg-blue-500/10 rounded-lg">
                    <Globe className="w-6 h-6 text-blue-500" />
                  </div>
                  <h2 className="text-2xl font-bold">What Remains and Alternatives</h2>
                </div>
                <div className="space-y-4 text-muted-foreground">
                  <div>
                    <h3 className="font-semibold text-foreground mb-1">Horizon Worlds (Meta)</h3>
                    <p>Meta's social VR platform became a new home for some AltspaceVR comedians and event hosts.</p>
                  </div>
                  <div>
                    <h3 className="font-semibold text-foreground mb-1">VRChat</h3>
                    <p>With a large, youthful user base, VRChat offers highly customisable avatars and worlds.</p>
                  </div>
                  <div>
                    <h3 className="font-semibold text-foreground mb-1">Engage and Spatial</h3>
                    <p>These platforms focus on professional events, education and collaboration.</p>
                  </div>
                  <div>
                    <h3 className="font-semibold text-foreground mb-1">Microsoft Mesh</h3>
                    <p>Still in development, Mesh aims to integrate holographic collaboration into Microsoft Teams.</p>
                  </div>
                </div>
              </article>
            </div>
          </section>

          {/* Sources */}
          <section className="mb-16">
            <h2 className="text-2xl font-bold mb-6">Sources</h2>
            <ul className="space-y-3 text-muted-foreground">
              <li className="flex items-start gap-2">
                <ArrowRight className="w-4 h-4 mt-1 text-primary flex-shrink-0" />
                <span>
                  <a href="https://www.vice.com/en/article/microsoft-shuts-down-legendary-vr-metaverse/" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Vice article</a> "Microsoft Shuts Down Legendary VR Metaverse," 
                  describing AltspaceVR's vibrant events and deep community connections.
                </span>
              </li>
              <li className="flex items-start gap-2">
                <ArrowRight className="w-4 h-4 mt-1 text-primary flex-shrink-0" />
                <span>
                  <a href="https://educatorsinvr.com/" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Educators in VR</a> blog post 
                  informing users about AltspaceVR's closure and how communities planned to preserve their worlds.
                </span>
              </li>
              <li className="flex items-start gap-2">
                <ArrowRight className="w-4 h-4 mt-1 text-primary flex-shrink-0" />
                <span>
                  <a href="https://siliconangle.com/2023/01/21/microsoft-close-social-vr-platform-altspacevr-part-10000-person-layoff/" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">SiliconAngle report</a> explaining 
                  that AltspaceVR's shutdown was linked to layoffs in Microsoft's mixed-reality division.
                </span>
              </li>
            </ul>
          </section>

          {/* CTA */}
          <section className="bg-gradient-to-br from-primary/10 via-accent/10 to-primary/10 rounded-2xl p-8 text-center border border-primary/20">
            <h2 className="text-2xl font-bold mb-4">Looking for an AltspaceVR Alternative?</h2>
            <p className="text-muted-foreground mb-6">
              While VR social platforms come and go, Chill Study Work offers a stable, browser-based community for focused study and collaboration.
            </p>
            <Link
              to="/altspacevr/"
              className="inline-flex items-center gap-2 bg-primary text-primary-foreground px-6 py-3 rounded-full font-semibold hover:opacity-90 transition-opacity"
            >
              Explore CSW as AltspaceVR Alternative
              <ArrowRight className="w-4 h-4" />
            </Link>
          </section>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default AltspaceVRHistory;
